import React, { useState, useEffect, lazy } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import chunk from 'lodash/chunk';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faCircleXmark } from '@fortawesome/free-solid-svg-icons';

import OrderItemReviewCard from 'Components/ProductReviews/OrderItemReviews/OrderItemReviewCard';
import sortByName from 'Helpers/Sort/SortByName';
import KeyLibrarySearch from 'Helpers/Order/KeyLibrarySearch';
import ConfirmAccount from 'Components/Account/ConfirmAccount';
import Paginate from 'Components/Utility/Paginate';
import Loading from 'Components/Utility/Loading';
import { history } from 'Redux/store';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import { supportUrl } from 'Config/lang';
import 'Components/ProductReviews/OrderItemReviews/OrderItemReviews.scss';
import { getSuggestedTagsByProductId } from 'Redux/ducks/product';
import LazyModal from 'Components/Utility/LazyModal';

const UserTagsModal = lazy(() => import(/* webpackChunkName: "UserTagsModal" */ 'Components/Product/UserTags/UserTagsModal'));

const messages = defineMessages({
  game: {
    id: 'orderitemreviews.game',
    defaultMessage: 'Game',
  },
  alphabetical: {
    id: 'orderitemreviews.alphabetical',
    defaultMessage: 'Alphabetical',
  },
  search_placeholder: {
    id: 'orderitemreviews.searchplaceholder',
    defaultMessage: 'Search product',
  },
  purchase_date: {
    id: 'orderitemreviews.purchasedate',
    defaultMessage: 'Purchase Date',
  },
  all: {
    id: 'orderitemreviews.all',
    defaultMessage: 'All',
  },
  dlc: {
    id: 'orderitemreviews.dlc',
    defaultMessage: 'DLC',
  },
  book: {
    id: 'orderitemreviews.book',
    defaultMessage: 'Book',
  },
  comic: {
    id: 'orderitemreviews.comic',
    defaultMessage: 'Comic',
  },
  software: {
    id: 'orderitemreviews.software',
    defaultMessage: 'Software',
  },
  elearning: {
    id: 'orderitemreviews.elearning',
    defaultMessage: 'eLearning',
  },
  tag_modal_title: {
    id: 'orderitemreviews.tag_modal_title',
    defaultMessage: 'Thank your for your review. Now help us tag {productName}.',
  },
  tag_modal_subtitle: {
    id: 'orderitemreviews.tag_modal_subtitle',
    defaultMessage: 'Help us define this {productType}. Choose the tags that are the most accurate and they will be added to the product page.',
  },
});

const OrderItemReviews = ({
  intl: { formatMessage },
  hasLoaded,
  fetchError,
  setItems,
  items,
  email,
  emailConfirmed,
  failedCheck,
  checkConfirmed,
  sendConfirmEmail,
  isAdmin,
}) => {
  const showUnreviewed = getUrlParameter('unreviewed') === 'true';
  const [sort, setSort] = useState('purchase');
  const [productType, setProductType] = useState('all');
  const [hideReviewed, setHideReviewed] = useState(showUnreviewed);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewStatus, setReviewStatus] = useState(false);
  const reviewStatusParam = getUrlParameter('reviewStatus') || '';
  const reviewPid = getUrlParameter('reviewPid') || '';
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagData, setTagData] = useState({});

  useEffect(() => {
    if (reviewStatusParam !== '') setReviewStatus(reviewStatusParam);

    const getTagData = async () => {
      const data = await getSuggestedTagsByProductId(reviewPid);
      setTagData(data);
      setTagModalOpen(true);
    };

    if (reviewPid) {
      getTagData();
    }
  }, [reviewStatusParam, reviewPid]);

  const onSortByChange = (value) => {
    setSort(value);
    history.push('/reviews');

    if (value === 'alphabetical') {
      const sortedItems = sortByName(items);
      setItems(sortedItems);
    }

    if (value === 'purchase') {
      const sortedItems = items.sort((a, b) => new Date(b.purchased) - new Date(a.purchased));
      setItems(sortedItems);
    }
  };

  const onFilterChange = (value, setFunc) => {
    setFunc(value);
    history.push('/reviews');
  };

  const updateHits = (pageToUpdate) => {
    if (pageToUpdate !== currentPage) {
      setCurrentPage(pageToUpdate);
    }
  };

  const clearSubmittedModal = () => {
    setReviewStatus('');
    history.push('/reviews');
  };

  const paidOrder = items.find(item => get(item, 'order.payment.status') !== 'REDEEM_CODE');

  let filteredItems = items;
  if (hideReviewed) filteredItems = filteredItems.filter(item => !item.reviewText);
  if (productType !== 'all') filteredItems = filteredItems.filter(item => item.type === productType);
  if (searchQuery !== '') filteredItems = KeyLibrarySearch(filteredItems, searchQuery);


  let chunkedItems = [];
  const nbPages = Math.ceil((filteredItems.length || 0) / 24);

  if (!isEmpty(filteredItems)) {
    chunkedItems = chunk(filteredItems, 24)[(currentPage - 1)];
  }

  return (
    <section className="account-content order-item-reviews-container">
      <h1>
        <FormattedMessage id="orderitemreviews.yourproductreviews" defaultMessage="My Product Reviews" />
      </h1>
      {!emailConfirmed && paidOrder &&
        <ConfirmAccount
          email={email}
          checkConfirmed={checkConfirmed}
          failedCheck={failedCheck}
          sendConfirmEmail={sendConfirmEmail}
        />
      }


      {reviewStatus &&
        <div className="review-submitted-container">
          <div className="close-button-container">
            <Button
              aria-label="close"
              color="link"
              className="close-button"
              onClick={clearSubmittedModal}
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </Button>
          </div>
          {reviewStatus === 'deleted' ?
            <div className="review-submitted-title"><FormattedMessage id="orderitemreviews.reviewdeleted" defaultMessage="Review Deleted" /></div>
            :

            <div className="review-submitted-title"><FormattedMessage id="orderitemreviews.reviewsubmitted" defaultMessage="Review Submitted" /></div>
          }
          <div>
            {reviewStatus === 'submitted' &&
              <FormattedMessage
                id="orderitemreviews.reviewsubmittedmessage"
                defaultMessage="Thank you for submitting your review. We are now processing it. This may take several days, so we appreciate your patience. We will notify you when this is complete. Please note that if the review contains any content that breaches our {reviewGuidelines} we will be unable to publish it."
                values={{
                  reviewGuidelines: <a className="brand-link" href={`${supportUrl()}/articles/4404673772177`} target="_blank" rel="noopener noreferrer">
                    <FormattedMessage id="orderitemreviews.reviewguidelineslink" defaultMessage="review guidelines" />
                  </a>,
                }}
              />
            }

            {reviewStatus === 'published' &&
              <FormattedMessage
                id="orderitemreviews.reviewpublishedmessage"
                defaultMessage="Thank you for submitting your review. Please note that if the review contains any content that breaches our {reviewGuidelines} it will be removed"
                values={{
                  reviewGuidelines: <a className="brand-link" href={`${supportUrl()}/articles/4404673772177`} target="_blank" rel="noopener noreferrer">
                    <FormattedMessage id="orderitemreviews.reviewguidelineslink" defaultMessage="review guidelines" />
                  </a>,
                }}
              />
            }

            {reviewStatus === 'deleted' &&
              <FormattedMessage
                id="orderitemreviews.reviewdeletedmessage"
                defaultMessage="Your review has been successfully deleted. it will no longer appear on the site. review. You can resubmit a new review at any time."
              />
            }

          </div>
        </div>
      }

      <div className="order-item-reviews-filter">
        <div className="drop-downs-section">
          <div className="dropdown-title-segment">
            <div className="mr-3"><FormattedMessage id="orderitemreviews.sortby" defaultMessage="Sort by" />:</div>
            <div className="dropdown-container sort-by">
              <select value={sort} onChange={e => onSortByChange(e.target.value)}>
                <option value="purchase">{formatMessage(messages.purchase_date)}</option>
                <option value="alphabetical">{formatMessage(messages.alphabetical)}</option>
              </select>
            </div>
          </div>
          <div className="dropdown-title-segment">
            <div className="mr-3"><FormattedMessage id="orderitemreviews.producttype" defaultMessage="Product Type" />:</div>
            <div className="dropdown-container sort-by">
              <select
                value={productType}
                onChange={e => onFilterChange(e.target.value, setProductType)}
              >
                <option value="all">{formatMessage(messages.all)}</option>
                <option value="game">{formatMessage(messages.game)}</option>
                <option value="dlc">{formatMessage(messages.dlc)}</option>
                <option value="book">{formatMessage(messages.book)}</option>
                <option value="software">{formatMessage(messages.software)}</option>
                <option value="comic">{formatMessage(messages.comic)}</option>
                <option value="elearning">{formatMessage(messages.elearning)}</option>
              </select>
            </div>
          </div>
          <div className="checkbox-container">
            <label htmlFor="hide-redeemed"><FormattedMessage id="orderitemreviews.hidereviewed" defaultMessage="Hide Reviewed" /></label>
            <input
              id="hide-redeemed"
              name="hide-redeemed"
              type="checkbox"
              checked={hideReviewed}
              aria-label="hide redeemed"
              onChange={e => onFilterChange(e.target.checked, setHideReviewed)}
            />
          </div>
        </div>
        <div className="autosearch-container">
          <div className="search-title"><FormattedMessage id="orderitemreviews.search" defaultMessage="search" />:</div>
          <div className="autosearch">
            <div className="input-group">
              <input
                type="text"
                aria-label="search"
                className="form-control autosearch-form"
                placeholder={formatMessage(messages.search_placeholder)}
                title={formatMessage(messages.search_placeholder)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                enterKeyHint="search"
                value={searchQuery}
                onChange={e => onFilterChange(e.target.value, setSearchQuery)}
              />
              <span className="input-group-append pr-3">
                <FontAwesomeIcon icon={faMagnifyingGlass} color="#bdbdbd" />
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="order-item-reviews-list">
        {hasLoaded && isEmpty(filteredItems) && !isEmpty(items) && !fetchError &&
          <div className="d-flex flex-column align-items-center mt-5">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <h3 className="pt-3">
              <FormattedMessage id="orderitemreviews.results.help" defaultMessage="We couldn't find any results for you." />
            </h3>
            <p>
              <FormattedMessage
                id="orderitemreviews.search-terms"
                defaultMessage="Try adjusting any applied filters or the terms of your search"
              />
            </p>
          </div>
        }
        {hasLoaded && isEmpty(items) && !fetchError &&
          <div className="text-center">
            <h3 className="pt-3">
              <FormattedMessage id="orderitemreviews.results.noproductstoreview" defaultMessage="You have no products to review" />
            </h3>
            <p>
              <FormattedMessage
                id="orderitemreviews.noproductreviewmessage"
                defaultMessage="Products that you have purchased will appear here. You will be given the option to review them."
              />
            </p>
          </div>
        }
        {hasLoaded && fetchError &&
          <div className="d-flex flex-column align-items-center mt-5">
            <h3 className="pt-3">
              <FormattedMessage id="orderitemreviews.results.error" defaultMessage="Error" />
            </h3>
            <p>
              <FormattedMessage
                id="orderitemreviews.error"
                defaultMessage="There was a problem fetching your product reviews. If the problem persists please contact support"
              />
            </p>
          </div>
        }
        {!hasLoaded && <Loading />}
        <div className="fan-grid-container left-align">
          {!isEmpty(chunkedItems) &&
            chunkedItems.map(item =>
              <OrderItemReviewCard
                key={Math.random()}
                item={item}
                status={get(item, 'order.status')}
                orderID={get(item, 'order._id')}
              />,
            )}
        </div>
      </div>
      <div className={`${nbPages <= 1 && currentPage <= 1 ? 'd-none' : ''}`}>
        {!isEmpty(items) && <Paginate
          hits={{ nbPages, page: currentPage - 1 }}
          updateHits={updateHits}
          pageLimit={100}
        />}</div>

      {!isEmpty(tagData) &&
        <LazyModal
          hasCloseFooter
          open={tagModalOpen}
          setOpen={setTagModalOpen}
          className="user-tags-modal"
          size="lg"
        >
          <UserTagsModal
            tags={tagData.tags}
            name={tagData.name}
            slug={tagData.slug}
            type={tagData.type}
            isAdmin={isAdmin}
            title={formatMessage(messages.tag_modal_title, { productName: tagData.name })}
            subtitle={formatMessage(messages.tag_modal_subtitle, { productType: tagData.type === 'game' ? formatMessage(messages.game).toLowerCase() : 'DLC' })}
          />
        </LazyModal>
      }

    </section>
  );
};


OrderItemReviews.propTypes = {
  setItems: PropTypes.func.isRequired,
  hasLoaded: PropTypes.bool,
  fetchError: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.object),
  email: PropTypes.string,
  emailConfirmed: PropTypes.bool.isRequired,
  failedCheck: PropTypes.bool,
  sendConfirmEmail: PropTypes.func.isRequired,
  checkConfirmed: PropTypes.func.isRequired,
  isAdmin: PropTypes.bool,
};

OrderItemReviews.defaultProps = {
  email: '',
  items: [],
  hasLoaded: false,
  fetchError: false,
  failedCheck: undefined,
  isAdmin: false,
};

export default injectIntl(OrderItemReviews);
